import dagre from "@dagrejs/dagre";
import type { Edge, Node } from "@xyflow/react";
import { computeEffectiveStatus, computeLinkState } from "@/features/network-topology/engine";
import type { LinkState, NetworkNode } from "@/features/network-topology/types";

/**
 * NetworkNode[] を React Flow の Node[] / Edge[] に変換し、
 * dagre で自動レイアウトする純粋関数（描画には依存しない）。
 *
 * - rankdir: TB (top-down)
 * - 各ノードは固定サイズ（描画側のカード幅に合わせる）
 * - エッジは両端ノードの effective から `LinkState` を導出し、
 *   className + animated で見た目を切り替える（CSS は network-flow.css）。
 * - エッジ id は `e-<childId>` で children と 1:1。React Flow から id を返してきたら
 *   そのまま selectedEdgeChildId として使える。
 * - 接続方式 (RJ45 等) はエッジ上には描画せず、選択時の EdgeDetailPanel に集約。
 */

export interface FlowGraph {
  nodes: Node[];
  edges: Edge[];
}

const NODE_WIDTH = 160;
const NODE_HEIGHT = 84;

const EDGE_CLASS: Record<LinkState, string> = {
  normal: "network-edge-normal",
  stopped: "network-edge-stopped",
  fault: "network-edge-fault",
  disconnected: "network-edge-disconnected",
};

/** エッジ id ⇄ 子ノード id の相互変換 */
export const edgeIdForChild = (childId: string): string => `e-${childId}`;
export const childIdFromEdgeId = (edgeId: string): string => (edgeId.startsWith("e-") ? edgeId.slice(2) : edgeId);

export function layoutNetworkAsFlow(
  networkNodes: readonly NetworkNode[],
  options: { selectedEdgeChildId?: string | null } = {},
): FlowGraph {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 32, ranksep: 88, marginx: 16, marginy: 16 });

  for (const n of networkNodes) {
    g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }

  const status = computeEffectiveStatus(networkNodes);

  const edges: Edge[] = [];
  for (const n of networkNodes) {
    if (n.parentId !== null) {
      g.setEdge(n.parentId, n.id);
      const parentStatus = status.get(n.parentId);
      const childStatus = status.get(n.id);
      const linkState: LinkState =
        parentStatus && childStatus
          ? computeLinkState(parentStatus, childStatus, n.linkDisconnected === true)
          : "stopped";

      const isSelected = options.selectedEdgeChildId === n.id;
      const className = `${EDGE_CLASS[linkState]}${isSelected ? " network-edge-selected" : ""}`;

      edges.push({
        id: edgeIdForChild(n.id),
        source: n.parentId,
        target: n.id,
        type: "default",
        // normal のみ React Flow 標準の流れる破線アニメ
        animated: linkState === "normal",
        className,
      });
    }
  }
  dagre.layout(g);

  const nodes: Node[] = networkNodes.map((n) => {
    const pos = g.node(n.id);
    return {
      id: n.id,
      type: "deviceNode",
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
      data: { networkNodeId: n.id },
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      draggable: false,
      connectable: false,
    };
  });

  return { nodes, edges };
}
